import { OnboardingAddVehicleDto, LabelType } from '@interfaces/models';
import { Typography, Separator, Button, toast } from '@ui/components';
import { Edit, Car } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useGetLabels } from '@shared/hooks';
import { useState, FC } from 'react';

import { EditVehicleDialog } from '../edit-vehicle-dialog/edit-vehicle-dialog';
import { VehicleForm } from '../../../hooks';

interface VehiclePreviewProps {
  vehicle: OnboardingAddVehicleDto;
  showSeparator: boolean;
  vehicleIndex: number;
  checkIfRegistrationNumberIsUnique: (
    registrationNumber: string,
    originalRegistrationNumber?: string,
  ) => boolean;
  handleEditVehicle: (vehicle: VehicleForm) => void;
  handleRemoveVehicle: () => void;
}

export const VehiclePreview: FC<VehiclePreviewProps> = (props) => {
  const {
    showSeparator,
    vehicleIndex,
    vehicle,
    checkIfRegistrationNumberIsUnique,
    handleRemoveVehicle,
    handleEditVehicle,
  } = props;

  const [openEditVehicleDialog, setOpenEditVehicleDialog] = useState(false);
  const { t } = useTranslation();

  const { getLabelName } = useGetLabels(LabelType.VEHICLE);

  const saveEditedVehicle = (editedVehicle: VehicleForm) => {
    const isRegistrationNumberUnique = checkIfRegistrationNumberIsUnique(
      editedVehicle.registrationNumber,
      vehicle.registrationNumber,
    );

    if (!isRegistrationNumberUnique) {
      toast.error(t('ONBOARDING.ADD_VEHICLES.REGISTRATION_NUMBER_NOT_UNIQUE'));
      return;
    }

    handleEditVehicle(editedVehicle);
    setOpenEditVehicleDialog(false);
  };

  return (
    <>
      <div className="rounded-lg px-4 py-3 border border-border-action mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Car className="size-4" />
          <Typography variant="h100">
            {t('ONBOARDING.ADD_VEHICLES.VEHICLE')} {vehicleIndex + 1}
          </Typography>

          <Button
            onClick={() => setOpenEditVehicleDialog(true)}
            className="ml-auto"
            variant="ghost"
          >
            <Edit className="size-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Typography
            className="truncate [&>span:last-child]:text-transparent"
            variant="labelMediumBook"
          >
            <span>{vehicle.registrationNumber}</span>
            <span className="px-2 text-border-default">|</span>
            <span>{vehicle.registrationCountry}</span>
            <span className="px-2 text-border-default">|</span>
            <span>{t(`VEHICLE_CATEGORY.${vehicle.vehicleCategory.toUpperCase()}`)}</span>
            <span className="px-2 text-border-default">|</span>
            {vehicle.name && (
              <>
                <span>{vehicle.name}</span> <span className="px-2 text-border-default">|</span>
              </>
            )}
            {!!vehicle.labels?.length && (
              <>
                {vehicle.labels?.map((label) => getLabelName(label)).join(', ')}
                <span className="px-2 text-border-default">|</span>
              </>
            )}
          </Typography>
        </div>
      </div>

      {showSeparator && <Separator className="my-8" />}

      <EditVehicleDialog
        onOpenChange={setOpenEditVehicleDialog}
        onRemove={handleRemoveVehicle}
        open={openEditVehicleDialog}
        onSave={saveEditedVehicle}
        vehicle={vehicle}
      />
    </>
  );
};
